import type { LucideIcon } from "lucide-react";

export type RentalType = "longue" | "moyenne" | "courte";
export type ChargesMode = "simple" | "detaille";

export interface DetailedCharges {
  taxeFonciere: number;
  assurancePNO: number;
  chargesCopro: number;
  entretien: number;
  cfe: number;
  fraisGestionPct: number; // % des loyers
  comptabilite: number;
  autres: number;
}

export interface SimulationInput {
  // Étape 1 — Le bien
  prixAcquisition: number;
  travaux: number;
  mobilier: number;
  region: string;

  // Étape 2 — Les revenus
  loyerMensuel: number;
  typeLocation: RentalType;
  tauxVacance: number; // 0-30 (en %)

  // Étape 3 — Les charges
  chargesMode: ChargesMode;
  chargesPct: number; // mode simple, 15-60
  chargesDetail: DetailedCharges;

  // Étape 4 — Le financement
  apport: number;
  hasCredit: boolean;
  montantEmprunte: number;
  tauxAnnuel: number; // %
  dureeAnnees: number;
  assuranceEmprunteurPct: number; // %

  // Mode avancé
  tmi: number; // 0.11 | 0.30 | 0.41 | 0.45
  inflationLoyers: number; // % annuel
  inflationCharges: number; // % annuel
  inflationBien: number; // % annuel (valorisation)
}

export const TMI_OPTIONS = [0.11, 0.30, 0.41, 0.45] as const;

export interface ComputedResults {
  investissementTotal: number;
  revenusBrutsAnnuels: number;
  revenusBrutsMensuels: number;
  // Crédit
  mensualiteCredit: number;
  interetsAnnee1: number;
  coutTotalInterets: number;
  assuranceAnnuelle: number;
  // Charges
  chargesTotales: number;       // charges d'exploitation (postes saisis/estimés, hors intérêts)
  chargesDeductibles: number;   // base fiscale = chargesTotales + intérêts + assurance prêt
  chargesDecaissees: number;    // = chargesTotales (intérêts déjà dans la mensualité)
  ratioCharges: number;
  // Cash-flow
  cashFlowAnnuel: number;
  cashFlowMensuel: number;
  // Amortissements
  amortissementBien: number;
  amortissementMobilier: number;
  amortissementAnnuel: number;
  // Fiscalité
  resultatFiscalReel: number;
  impotsReel: number;
  impotsMicroBIC: number;
  resultatNetReel: number;
  resultatNetMicroBIC: number;
  // Rendements
  rendementBrut: number;
  rendementNet: number;
  roiApport: number | null;
  // Score
  score: number;
  scoreLabel: string;
  scoreColor: "danger" | "warning" | "yellow" | "successLight" | "success";
  // Pour graphiques
  chargesBreakdown: { name: string; value: number; color: string }[];
  // Projection
  projection: ProjectionYear[];
}

export interface ProjectionYear {
  annee: number;
  cashFlowAnnuel: number;
  cashFlowCumule: number;
  capitalRembourseCumule: number;
  capitalRestantDu: number;
  valeurBien: number;
  patrimoineNet: number;
}

export interface DiagnosticInsight {
  id: string;
  type: "positive" | "alert" | "opportunity";
  icon: LucideIcon;
  title: string;
  priority: number; // plus élevé = plus important
  affiliate?: "credit" | "assurance"; // affiche une AffiliateCard sous l'insight
}

export const REGIONS = [
  "Auvergne-Rhône-Alpes",
  "Bourgogne-Franche-Comté",
  "Bretagne",
  "Centre-Val de Loire",
  "Corse",
  "Grand Est",
  "Hauts-de-France",
  "Île-de-France",
  "Normandie",
  "Nouvelle-Aquitaine",
  "Occitanie",
  "Pays de la Loire",
  "Provence-Alpes-Côte d'Azur",
  "Guadeloupe",
  "Martinique",
  "Guyane",
  "La Réunion",
  "Mayotte",
] as const;

export const DEFAULT_VACANCE: Record<RentalType, number> = {
  longue: 5,
  moyenne: 10,
  courte: 20,
};

export const TMI = 0.30;
export const PRELEVEMENTS_SOCIAUX = 0.172;
